import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DefaultAuth from "../../../components/auth/Default";
import client from "../../../helpers/Client";
import AuthContext from "../../../contexts/Auth";

import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Text,
  FormHelperText,
  InputGroup,
  InputLeftElement,
  HStack,
  useToast,
  SlideFade,
  ScaleFade,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "@chakra-ui/react";
import { FiUser, FiMessageSquare, FiArrowLeft, FiHash, FiLock } from "react-icons/fi";
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from "../../../components/LanguageSwitcher";

function SignIn() {
  const textColor = "navy.700";
  const textColorSecondary = "gray.400";
  const cardColor = 'gray.50';
  const brandStars = "red.800";
  const [username, setUsername] = useState("");
  const [resellerCode, setResellerCode] = useState("");
  const [password, setPassword] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [smsRequired, setSmsRequired] = useState(false);
  const [smsSessionToken, setSmsSessionToken] = useState("");
  const [smsCode, setSmsCode] = useState("");
  const [twoFAChannel, setTwoFAChannel] = useState("sms");
  const [rememberMe, setRememberMe] = useState(false);
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();
  const toast = useToast();
  const { t } = useTranslation();
  const [trustOpen, setTrustOpen] = useState(false);
  const [deviceFp, setDeviceFp] = useState("");
  const [deviceMeta, setDeviceMeta] = useState({});
  const [pendingUserData, setPendingUserData] = useState(null);
  const handleBack = () => {
    setSmsRequired(false);
    setSmsCode("");
    setSmsSessionToken("");
    setError("");
  };

  // Sayfa yüklendiğinde kaydedilmiş kullanıcı adını yükle
  useEffect(() => {
    const savedUsername = localStorage.getItem('rememberedUsername');
    const savedRememberMe = localStorage.getItem('rememberMe') === 'true';

    if (savedRememberMe && savedUsername) {
      setUsername(savedUsername);
      setRememberMe(true);
    }
  }, []);

  const handleFormSubmit = (e) => {
    e.preventDefault();
    handleSignIn();
  };
  const getDeviceInfo = async () => {
    const nav = navigator || {};
    const screenObj = window && window.screen ? window.screen : {};
    const gl = (() => {
      try {
        const canvas = document.createElement("canvas");
        const gl = canvas.getContext("webgl") || canvas.getContext("experimental-webgl");
        if (!gl) return {};
        const debugInfo = gl.getExtension("WEBGL_debug_renderer_info");
        return debugInfo
          ? {
            vendor: gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL),
            renderer: gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL),
          }
          : {};
      } catch {
        return {};
      }
    })();
    const meta = {
      userAgent: nav.userAgent || "",
      platform: nav.platform || "",
      language: nav.language || "",
      languages: Array.isArray(nav.languages) ? nav.languages.join(",") : "",
      vendor: nav.vendor || "",
      hardwareConcurrency: nav.hardwareConcurrency || 0,
      deviceMemory: nav.deviceMemory || 0,
      screen: {
        w: screenObj.width || 0,
        h: screenObj.height || 0,
        aw: screenObj.availWidth || 0,
        ah: screenObj.availHeight || 0,
        cd: screenObj.colorDepth || 0,
        pr: window.devicePixelRatio || 1,
      },
      tzOffset: new Date().getTimezoneOffset(),
      gl,
      touch: "ontouchstart" in window,
    };
    const text = JSON.stringify(meta);
    try {
      if (window.crypto && window.crypto.subtle) {
        const enc = new TextEncoder();
        const data = enc.encode(text);
        const buf = await window.crypto.subtle.digest("SHA-256", data);
        const bytes = Array.from(new Uint8Array(buf));
        const fp = bytes.map(b => b.toString(16).padStart(2, "0")).join("");
        return { fp, meta };
      }
      // Fallback hash (non-cryptographic, works in non-secure contexts)
      let h1 = 0x811c9dc5, h2 = 0x1b873593;
      for (let i = 0; i < text.length; i++) {
        const c = text.charCodeAt(i);
        h1 ^= c; h1 = (h1 * 0x01000193) >>> 0;
        h2 ^= c; h2 = (h2 * 0x85ebca6b) >>> 0;
      }
      const fp = (h1.toString(16).padStart(8, "0") + h2.toString(16).padStart(8, "0")).padEnd(64, "0");
      return { fp, meta };
    } catch {
      const fp = (Date.now().toString(16) + Math.random().toString(16).slice(2)).slice(0, 64).padEnd(64, "0");
      return { fp, meta };
    }
  };
  const handleSignIn = async () => {
    if (!username) { setError(t('login.errors.username_required')); return; }
    if (!resellerCode) { setError(t('login.errors.reseller_code_required')); return; }
    if (!password) { setError(t('login.errors.password_required') || 'Şifre gereklidir.'); return; }
    setLoading(true);
    const { fp, meta } = await getDeviceInfo();
    setDeviceFp(fp);
    setDeviceMeta(meta);

    client
      .post(
        "/auth/login/",
        { username, reseller_code: resellerCode, password, device_fingerprint: fp, device_info: meta }
      )
      .then((response) => {
        const data = response.data || {};
        if (response.status === 202 && (data.requires_sms || data.requires_email)) {
          setSmsRequired(true);
          setSmsSessionToken(data.session_token || "");
          setTwoFAChannel(data.requires_email ? "email" : "sms");
          setLoading(false);
          return;
        }
        return data;
      })
      .then((userData) => {
        if (!userData) return; // SMS adımı bekleniyor
        login(userData);
        if (rememberMe) {
          localStorage.setItem('rememberedUsername', username);
          localStorage.setItem('rememberMe', 'true');
        } else {
          localStorage.removeItem('rememberedUsername');
        }
        setLoading(false);
        if (userData?.device_trusted) {
          navigate("/ana-sayfa/");
          return;
        }
        setPendingUserData(userData);
        setTrustOpen(true);
      })
      .catch((error) => {
        setLoading(false);
        const msg = error?.response?.data?.detail || t('login.errors.invalid_username');
        setError(msg);
      });
  };

  const handleVerifySms = () => {
    if (!smsCode) { setError(t('login.errors.sms_code_required')); return; }
    setLoading(true);
    const payload = { username, reseller_code: resellerCode, session_token: smsSessionToken, device_fingerprint: deviceFp, device_info: deviceMeta };
    if (twoFAChannel === "sms") {
      payload.sms_code = smsCode;
    } else {
      payload.code = smsCode;
    }
    client
      .post(
        "/auth/login/",
        payload
      )
      .then((response) => response.data)
      .then((userData) => {
        login(userData);
        setLoading(false);
        if (userData?.device_trusted) {
          navigate("/ana-sayfa/");
          return;
        }
        setPendingUserData(userData);
        setTrustOpen(true);
        setSmsRequired(false);
        setSmsCode("");
        setSmsSessionToken("");
      })
      .catch(() => {
        setLoading(false);
        setError(t('login.errors.sms_verification_failed'));
      });
  };

  const handleResendSms = () => {
    if (!username) { setError(t('login.errors.username_required')); return; }
    if (!resellerCode) { setError(t('login.errors.reseller_code_required')); return; }
    setLoading(true);
    client
      .post(
        "/auth/login/",
        { username, reseller_code: resellerCode, password }
      )
      .then((response) => {
        const data = response.data || {};
        setSmsRequired(true);
        setSmsSessionToken(data.session_token || "");
        setTwoFAChannel(data.requires_email ? "email" : "sms");
        toast({ title: t('login.sms_resend_success'), status: "success", duration: 2000 });
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        setError(t('login.errors.sms_resend_failed'));
      });
  };

  const resendViaAlternateChannel = (channel) => {
    if (!username) { setError(t('login.errors.username_required')); return; }
    if (!resellerCode) { setError(t('login.errors.reseller_code_required')); return; }
    setLoading(true);
    client
      .post("/auth/login/", { username, reseller_code: resellerCode, password, prefer_channel: channel })
      .then((response) => {
        const data = response.data || {};
        setSmsRequired(true);
        setSmsSessionToken(data.session_token || "");
        setTwoFAChannel(data.requires_email ? "email" : "sms");
        toast({
          title: channel === "sms" ? t('login.sms_resend_success') : t('login.email_resend_success'),
          status: "success",
          duration: 2000
        });
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        setError(channel === "sms" ? t('login.errors.sms_resend_failed') : t('login.errors.email_resend_failed'));
      });
  };

  // Theme management removed - using light mode only



  const boxStyle = {
    boxShadow: '2xl',
    borderRadius: '16px',
    padding: '24px'
  };

  return (
    <form onSubmit={handleFormSubmit}>
      <Modal isOpen={trustOpen} onClose={() => { setTrustOpen(false); navigate('/ana-sayfa/'); }}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Bu cihazı güvenilir olarak kaydetmek ister misiniz?</ModalHeader>
          <ModalBody>
            <Text fontSize="sm" color="gray.600">Bu cihazı güvenilir olarak işaretlerseniz, bu cihazdan girişlerde 2 aşamalı doğrulama istemeyiz.</Text>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={() => { setTrustOpen(false); navigate('/ana-sayfa/'); }}>Hayır</Button>
            <Button colorScheme="blue" onClick={async () => {
              try {
                const uidUrl = pendingUserData?.user?.url || "";
                const parts = String(uidUrl).split("/").filter(Boolean);
                const uid = parts[parts.length - 1];
                await client.post(`/users/${uid}/trusted_devices/`, { fingerprint: deviceFp, name: deviceMeta.platform || "Browser", meta: deviceMeta, trusted: true });
                toast({ title: "Cihaz güvenilir olarak kaydedildi", status: "success", duration: 2000 });
              } catch {
                toast({ title: "Kayıt başarısız", status: "error", duration: 2000 });
              } finally {
                setTrustOpen(false);
                navigate("/ana-sayfa/");
              }
            }}>Evet, kaydet</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <DefaultAuth
        illustrationBackground={"https://inohom.b-cdn.net/newImages/inohomtrlogo.png"}
      >
        <Flex
          style={boxStyle}
          bg={cardColor}
          maxW={{ base: "max-content", md: "max-content" }}
          boxShadow='xl' p='6' rounded='md'
          borderRadius="10px"
          alignItems="start"
          justifyContent="center"
          flexDirection="column"
        >

          <Box me="auto">
            {smsRequired && (
              <Button
                onClick={handleBack}
                size="xs"
                variant="ghost"
                leftIcon={<FiArrowLeft />}
                colorScheme="gray"
                mb="2"
              >
                {t('login.back')}
              </Button>
            )}
            <HStack justifyContent="space-between" w="100%" mb="9">
              <Heading color={textColor} fontSize="36px" >
                {t('login.title')}

              </Heading>
              <Box />
              <LanguageSwitcher />
            </HStack>


            <Text
              mb="36px"
              ms="4px"
              color={textColorSecondary}
              fontWeight="400"
              fontSize="md"
            >
              {t('login.subtitle')}
            </Text>

          </Box>
          <Flex
            direction="column"
            w={{ base: "100%", md: "420px" }}
            maxW="100%"
            background="transparent"
            borderRadius="15px"
            mx={{ base: "auto", lg: "unset" }}
            me="auto"
            mb={{ base: "10px", md: "auto" }}
          >
            <FormControl>
              <FormLabel
                display="flex"
                ms="4px"
                fontSize="sm"
                fontWeight="500"
                color={textColor}
                mb="8px"
              >
                {smsRequired ? (
                  <>{t('login.sms_code')}<Text color={brandStars}>*</Text></>
                ) : (
                  <>{t('login.username')}<Text color={brandStars}>*</Text></>
                )}
              </FormLabel>
              {smsRequired ? (
                <SlideFade in={smsRequired} offsetY="10px">
                  <ScaleFade in={smsRequired} initialScale={0.96}>
                    <Text fontSize="sm" color="gray.600" mb="2">
                      {twoFAChannel === "sms" ? t('login.twofa_hint_sms') : t('login.twofa_hint_email')}
                    </Text>
                    <InputGroup size="lg">
                      <InputLeftElement pointerEvents="none">
                        <FiMessageSquare color="#999" />
                      </InputLeftElement>
                      <Input
                        fontSize="sm"
                        mb="8px"
                        type="text"
                        value={smsCode}
                        onChange={(e) => setSmsCode(e.target.value)}
                        placeholder={t('login.sms_code_placeholder')}
                        focusBorderColor="blue.700"
                      />
                    </InputGroup>
                  </ScaleFade>
                </SlideFade>
              ) : (
                <SlideFade in={!smsRequired} offsetY="-10px">
                  <ScaleFade in={!smsRequired} initialScale={0.96}>
                    <InputGroup size="lg">
                      <InputLeftElement pointerEvents="none">
                        <FiUser color="#999" />
                      </InputLeftElement>
                      <Input
                        isRequired={true}
                        fontSize="sm"
                        ms={{ base: "0px", md: "0px" }}
                        type="text"
                        placeholder={t('login.username_placeholder')}
                        mb="8px"
                        fontWeight="500"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        focusBorderColor="red.700"
                      />
                    </InputGroup>
                    <FormHelperText>{t('login.username_help')}</FormHelperText>
                    <FormLabel
                      display="flex"
                      ms="4px"
                      fontSize="sm"
                      fontWeight="500"
                      color={textColor}
                      mb="8px"
                      mt="16px"
                    >
                      <>{t('login.reseller_code')}<Text color={brandStars}>*</Text></>
                    </FormLabel>
                    <InputGroup size="lg">
                      <InputLeftElement pointerEvents="none">
                        <FiHash color="#999" />
                      </InputLeftElement>
                      <Input
                        isRequired={true}
                        fontSize="sm"
                        type="text"
                        placeholder={t('login.reseller_code_placeholder')}
                        mb="8px"
                        fontWeight="500"
                        value={resellerCode}
                        onChange={(e) => setResellerCode(e.target.value)}
                        focusBorderColor="red.700"
                      />
                    </InputGroup>
                    <FormHelperText>{t('login.reseller_code_help')}</FormHelperText>
                    <FormLabel
                      display="flex"
                      ms="4px"
                      fontSize="sm"
                      fontWeight="500"
                      color={textColor}
                      mb="8px"
                      mt="16px"
                    >
                      <>Şifre<Text color={brandStars}>*</Text></>
                    </FormLabel>
                    <InputGroup size="lg">
                      <InputLeftElement pointerEvents="none">
                        <FiLock color="#999" />
                      </InputLeftElement>
                      <Input
                        isRequired={true}
                        fontSize="sm"
                        type="password"
                        placeholder="Şifrenizi girin"
                        mb="8px"
                        fontWeight="500"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        focusBorderColor="red.700"
                      />
                    </InputGroup>
                  </ScaleFade>
                </SlideFade>
              )}
            </FormControl>
            {error && (
              <Text as='b' fontSize='md' color="red.500" mt="2">
                {error}
              </Text>
            )}
            <Flex justifyContent="space-between" align="center" mb="24px">
              <FormControl display="flex" alignItems="center">
                <Checkbox
                  id="remember-login"
                  me="10px"
                  isChecked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <FormLabel
                  htmlFor="remember-login"
                  mb="0"
                  fontWeight="normal"
                  color={textColor}
                  fontSize="sm"
                >
                  {t('login.remember_me')}
                </FormLabel>

              </FormControl>



            </Flex>


            {!smsRequired && (
              <Button
                bg="red.700"
                _hover={{ bg: "red.800" }}
                color="#fff"
                fontSize="sm"
                borderRadius={9}
                onClick={handleSignIn}
                fontWeight="600"
                w="100%"
                h="50"
                isLoading={loading}
                mb="24px"
                isDisabled={!username || !resellerCode || !password}
                type="submit"
              >
                {t('login.continue')}
              </Button>
            )}

            {smsRequired && (
              <>
                <HStack spacing={4} mb="12px">
                  {twoFAChannel === "email" ? (
                    <Button
                      variant="outline"
                      colorScheme="blue"
                      borderRadius={9}
                      onClick={() => resendViaAlternateChannel("sms")}
                      fontWeight="600"
                      w="100%"
                      h="50"
                      isLoading={loading}
                      type="button"
                    >
                      {t('login.try_sms')}
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      colorScheme="blue"
                      borderRadius={9}
                      onClick={() => resendViaAlternateChannel("email")}
                      fontWeight="600"
                      w="100%"
                      h="50"
                      isLoading={loading}
                      type="button"
                    >
                      {t('login.try_email')}
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    colorScheme="gray"
                    borderRadius={9}
                    onClick={handleResendSms}
                    fontWeight="600"
                    w="50%"
                    h="50"
                    isLoading={loading}
                    type="button"
                  >
                    {t('login.resend_sms')}
                  </Button>
                </HStack>
                <HStack spacing={4} mb="12px">

                  <Button
                    bg="#004466"
                    _hover={{ bg: "#005a80" }}
                    color={"#fff"}
                    fontSize="sm"
                    borderRadius={9}
                    onClick={handleVerifySms}
                    fontWeight="600"
                    w="100%"
                    h="50"
                    isLoading={loading}
                    isDisabled={!smsCode}
                    type="button"
                  >
                    {t('login.verify_sms')}
                  </Button>
                </HStack>
              </>
            )}


            <Flex
              flexDirection="column"
              justifyContent="center"
              alignItems="start"
              maxW="100%"
              mt="0px"
            >

            </Flex>
          </Flex>

        </Flex>
      </DefaultAuth>
    </form>
  );
}

export default SignIn;
